import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { InputField } from "../../components/common/input-field";
import { SelectField } from "./SelectField";

import { eInvoiceSchema } from "./schema/eInvoice.schema";

import type { EInvoice } from "@/types/account/einvoice";
import type { EInvoiceValues } from "./schema/eInvoice.schema";
import { SearchableDropdown } from "@/components/common/SearchableDropdown";
import {
  GetStateCountryByCity,
  UpdateEInvoice,
} from "@/services/account.service";
import { useAuthStore } from "@/stores/auth.store";
import { cn } from "@/lib/utils";
import PhoneField from "@/components/common/PhoneField";
import type { DialCode } from "@/types/dialcode";

const FIELD_CLASS =
  "h-14 rounded-2xl border-border bg-card px-4 text-sm font-medium text-muted-foreground placeholder:text-border";

const ID_TYPE_OPTIONS = ["NRIC", "BRN"];

interface EInvoiceFormProps {
  eInvoice?: EInvoice;
  dialCodes: DialCode[];
  loadingDialCodes: boolean;
}

export function EInvoiceForm({
  eInvoice,
  dialCodes,
  loadingDialCodes,
}: EInvoiceFormProps) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<EInvoiceValues>({
    resolver: zodResolver(eInvoiceSchema),
    mode: "onChange",
    defaultValues: {
      registrationName: "",
      tinNo: "",
      sstNo: "",
      idType: "",
      nricBrnNo: "",
      msicCode: "",
      contactNoCountryCode: "",
      contactNo: "",
      email: "",
      address1: "",
      address2: "",
      cityId: "",
      city: "",
      postcode: "",
      stateId: "",
      state: "",
      countryId: "",
      country: "",
      businessActivities: "",
    },
  });

  const idType = useWatch({ control, name: "idType" });
  const city = useWatch({ control, name: "city" });
  const contactNoCountryCode =
    useWatch({ control, name: "contactNoCountryCode" }) ?? "";
  const contactNo = useWatch({ control, name: "contactNo" }) ?? "";
  const requiredValues = useWatch({
    control,
    name: [
      "registrationName",
      "tinNo",
      "idType",
      "nricBrnNo",
      "contactNoCountryCode",
      "contactNo",
      "email",
      "address1",
      "city",
      "postcode",
      "state",
      "country",
      "businessActivities",
    ],
  });
  const requiredFieldsComplete = requiredValues.every((value) =>
    typeof value === "string" ? value.trim().length > 0 : Boolean(value),
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!eInvoice) return;

    reset({
      registrationName: eInvoice.registrationName,
      tinNo: eInvoice.tinNo,
      sstNo: eInvoice.sstNo ?? "",
      idType: eInvoice.idType,
      nricBrnNo: eInvoice.registrationIdNo,
      msicCode: eInvoice.msicCode ?? "",
      contactNoCountryCode: eInvoice.contactNoCountryCode ?? "",
      contactNo: eInvoice.contactNo ?? "",
      email: eInvoice.email,
      address1: eInvoice.addressLine1,
      address2: eInvoice.addressLine2 ?? "",
      cityId: eInvoice.mdCityId,
      city: eInvoice.mdCityId,
      postcode: eInvoice.postcode,
      stateId: eInvoice.mdStateId,
      state: eInvoice.mdStateName,
      countryId: eInvoice.mdCountryId,
      country: eInvoice.mdCountryName,
      businessActivities: eInvoice.businessActivity,
    });
  }, [eInvoice, reset]);

  const handleCityChange = async (cityId: string) => {
    setValue("city", cityId, {
      shouldValidate: true,
      shouldDirty: true,
    });

    setValue("cityId", cityId, {
      shouldValidate: true,
      shouldDirty: true,
    });

    try {
      const response = await GetStateCountryByCity({
        mdCityId: cityId,
      });

      const data = response.data;

      setValue("stateId", data.mdStateId, {
        shouldValidate: true,
        shouldDirty: true,
      });

      setValue("state", data.mdStateName, {
        shouldValidate: true,
        shouldDirty: true,
      });

      setValue("countryId", data.mdCountryId, {
        shouldValidate: true,
        shouldDirty: true,
      });

      setValue("country", data.mdCountryName, {
        shouldValidate: true,
        shouldDirty: true,
      });
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to get state and country.",
      );
    }
  };

  const onSubmit = async (data: EInvoiceValues) => {
    const agentCompanySellerId =
      useAuthStore.getState().user?.agentCompanySellerId;

    if (!agentCompanySellerId) {
      toast.error("Missing agent company seller ID.");
      return;
    }

    try {
      setSaving(true);

      await UpdateEInvoice({
        agentCompanySellerId,
        registrationName: data.registrationName,
        tinNo: data.tinNo,
        sstNo: data.sstNo ?? "",
        idType: data.idType,
        registrationIdNo: data.nricBrnNo,
        msicCode: data.msicCode ?? "",
        contactNoCountryCode: data.contactNoCountryCode,
        contactNo: data.contactNo,
        email: data.email,
        addressLine1: data.address1,
        addressLine2: data.address2 ?? "",
        mdCityId: data.cityId,
        postcode: data.postcode,
        mdStateId: data.stateId,
        mdCountryId: data.countryId,
        businessActivity: data.businessActivities ?? "",
      });

      toast.success("E-Invoice information updated successfully.");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to update E-Invoice information.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-2">
      <div className="text-lg font-semibold text-secondary-foreground">
        E-Invoice Information
      </div>

      <div className="rounded-2xl bg-card px-4 py-6 shadow-xl">
        <div className="flex flex-col gap-4 md:grid md:grid-cols-3">
          {/* Registration Name */}
          <InputField
            label="Registration Name"
            required
            placeholder="Registration Name"
            {...register("registrationName")}
            className={cn(
              FIELD_CLASS,
              errors.registrationName && "border-destructive",
            )}
            error={errors.registrationName?.message}
          />

          {/* TIN No. */}
          <InputField
            label="TIN No."
            required
            placeholder="TIN No."
            {...register("tinNo", {
              onChange: (e) => {
                e.target.value = e.target.value.toUpperCase();
              },
            })}
            className={cn(FIELD_CLASS, errors.tinNo && "border-destructive")}
            error={errors.tinNo?.message}
          />

          {/* SST No. */}
          <InputField
            label="SST No."
            placeholder="SST No."
            {...register("sstNo", {
              onChange: (e) => {
                e.target.value = e.target.value.toUpperCase();
              },
            })}
            className={FIELD_CLASS}
            error={errors.sstNo?.message}
          />

          {/* ID Type */}
          <SelectField
            label="ID Type"
            required
            placeholder="ID Type"
            value={idType}
            options={ID_TYPE_OPTIONS}
            className={FIELD_CLASS}
            onSelect={(value) => {
              setValue("idType", value, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }}
          />

          {/* NRIC / BRN No. */}
          <InputField
            label="NRIC / BRN No."
            required
            placeholder={
              idType === "NRIC" ? "XXXXXX-XX-XXXX" : "NRIC / BRN No."
            }
            {...register("nricBrnNo", {
              onChange: (e) => {
                let value = e.target.value.replace(/\D/g, "");

                if (idType === "NRIC") {
                  value = value.slice(0, 12);

                  if (value.length > 6) {
                    value = `${value.slice(0, 6)}-${value.slice(6)}`;
                  }

                  if (value.length > 9) {
                    value = `${value.slice(0, 9)}-${value.slice(9)}`;
                  }
                }

                e.target.value = value;
              },
            })}
            className={cn(
              FIELD_CLASS,
              errors.nricBrnNo && "border-destructive",
            )}
            error={errors.nricBrnNo?.message}
          />

          {/* MSIC Code */}
          <InputField
            label="MSIC Code"
            placeholder="MSIC Code"
            {...register("msicCode")}
            className={cn(FIELD_CLASS, errors.msicCode && "border-destructive")}
            error={errors.msicCode?.message}
          />

          {/* Contact No. */}
          <PhoneField
            code={contactNoCountryCode || "Country Code"}
            phone={contactNo}
            dialCodes={dialCodes}
            onCodeChange={(value) => {
              setValue("contactNoCountryCode", value, {
                shouldValidate: true,
                shouldDirty: true,
              });

              trigger(["contactNoCountryCode", "contactNo"]);
            }}
            onPhoneChange={(value) => {
              // Keep maximum input at 15 digits
              const phone = value.replace(/\D/g, "").slice(0, 15);

              // Don't allow 0 as first digit for Malaysia
              if (contactNoCountryCode === "+60" && phone.startsWith("0")) {
                return;
              }

              setValue("contactNo", phone, {
                shouldValidate: true,
                shouldDirty: true,
              });

              if (phone && !contactNoCountryCode) {
                trigger(["contactNoCountryCode", "contactNo"]);
                return;
              }

              // Validate immediately while typing
              trigger("contactNo");
            }}
            error={
              errors.contactNo?.message || errors.contactNoCountryCode?.message
            }
          />

          {/* Email */}
          <InputField
            label="Email"
            required
            placeholder="Email"
            type="email"
            {...register("email")}
            className={cn(FIELD_CLASS, errors.email && "border-destructive")}
            error={errors.email?.message}
          />

          {/* Address 1 */}
          <div className="md:col-span-3">
            <InputField
              label="Address"
              required
              placeholder="Business / Residential Address 1"
              {...register("address1")}
              className={cn(
                FIELD_CLASS,
                errors.address1 && "border-destructive",
              )}
              error={errors.address1?.message}
            />
            <div className="mt-4">
              <InputField
                label=""
                placeholder="Business / Residential Address 2"
                {...register("address2")}
                className={cn(
                  FIELD_CLASS,
                  errors.address2 && "border-destructive",
                )}
                error={errors.address2?.message}
              />
            </div>
          </div>

          {/* City + Postcode */}
          <div className="grid grid-cols-2 gap-4 md:contents">
            {/* City */}
            <SearchableDropdown
              label="City"
              required
              placeholder="Select City"
              searchPlaceholder="Search city..."
              value={city}
              options={[
                ...(eInvoice?.mdCityId && eInvoice?.mdCityName
                  ? [
                      {
                        value: eInvoice.mdCityId,
                        label: eInvoice.mdCityName,
                      },
                    ]
                  : []),
                ...(eInvoice?.cityList ?? [])
                  .filter((item) => item.mdCityId !== eInvoice?.mdCityId)
                  .map((item) => ({
                    value: item.mdCityId,
                    label: item.name,
                  })),
              ]}
              onSelect={handleCityChange}
              error={errors.city?.message}
            />

            <div>
              <InputField
                label="Postcode"
                required
                placeholder="Postcode"
                maxLength={5}
                {...register("postcode", {
                  onChange: (e) => {
                    e.target.value = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 5);
                  },
                })}
                className={cn(
                  FIELD_CLASS,
                  errors.postcode && "border-destructive",
                )}
                error={errors.postcode?.message}
              />
            </div>
          </div>

          <input type="hidden" {...register("cityId")} />

          {/* State + Country */}
          <div className="grid grid-cols-2 gap-4 md:contents">
            {/* State */}
            <InputField
              label="State"
              required
              placeholder="State"
              {...register("state")}
              className={cn(FIELD_CLASS, errors.state && "border-destructive")}
              error={errors.state?.message}
              disabled
            />

            <input type="hidden" {...register("stateId")} />

            {/* Country */}
            <InputField
              label="Country"
              required
              placeholder="Country"
              {...register("country")}
              className={cn(
                FIELD_CLASS,
                errors.country && "border-destructive",
              )}
              error={errors.country?.message}
              disabled
            />

            <input type="hidden" {...register("countryId")} />
          </div>

          {/* Business Activities */}
          <InputField
            label="Business Activities"
            required
            placeholder="Business Activities"
            {...register("businessActivities")}
            className={cn(
              FIELD_CLASS,
              errors.businessActivities && "border-destructive",
            )}
            error={errors.businessActivities?.message}
          />
        </div>

        {/* Submit */}
        <div className="mt-4 flex justify-end">
          <Button
            type="submit"
            disabled={
              !requiredFieldsComplete ||
              isSubmitting ||
              saving ||
              loadingDialCodes
            }
            className="w-full cursor-pointer md:w-2xs"
          >
            {isSubmitting || saving ? (
              <>
                <Loader2 className="animate-spin" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </div>
      </div>
    </form>
  );
}
